/**
 * Small graph helpers for formula dependencies. Nodes are ids; `deps(id)` lists what a node
 * depends on (edges point from a node to its dependencies).
 */

/** Returns one dependency cycle (as a closed path, first === last) or null. */
export function findCycle<T>(nodes: readonly T[], deps: (node: T) => readonly T[]): T[] | null {
  const state = new Map<T, 'visiting' | 'done'>();
  const stack: T[] = [];

  const visit = (node: T): T[] | null => {
    const s = state.get(node);
    if (s === 'done') return null;
    if (s === 'visiting') {
      const from = stack.indexOf(node);
      return [...stack.slice(from), node];
    }
    state.set(node, 'visiting');
    stack.push(node);
    for (const dep of deps(node)) {
      const cycle = visit(dep);
      if (cycle) return cycle;
    }
    stack.pop();
    state.set(node, 'done');
    return null;
  };

  for (const node of nodes) {
    const cycle = visit(node);
    if (cycle) return cycle;
  }
  return null;
}

/**
 * Orders `nodes` so every node comes after its dependencies (dependencies outside `nodes` are
 * ignored). Ties keep the input order, so the result is deterministic. Throws on a cycle.
 */
export function topoSort<T>(nodes: readonly T[], deps: (node: T) => readonly T[]): T[] {
  const inSet = new Set(nodes);
  const done = new Set<T>();
  const visiting = new Set<T>();
  const out: T[] = [];

  const visit = (node: T): void => {
    if (done.has(node)) return;
    if (visiting.has(node)) throw new Error('Dependency cycle');
    visiting.add(node);
    for (const dep of deps(node)) {
      if (inSet.has(dep)) visit(dep);
    }
    visiting.delete(node);
    done.add(node);
    out.push(node);
  };

  for (const node of nodes) visit(node);
  return out;
}
